import React, { useEffect, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import { runSimulations } from '@/api/testing'
import { Spinner } from '@/components/Spinner'
import * as Plot from '@observablehq/plot'
import {
  MealsDayDeviationRow,
  RecommenderSettings,
  TestingResponse,
  GeneratorOptions,
  DistinctRecipes,
  NonRecommendedRecipe,
} from '@/api/types'
import { Label } from '@/components/Label'
import { Button } from '@/components/Button'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { RecommenderSettingsForm } from '@/features/RecommenderSettingsForm/RecommenderSettingsForm'
import { recommenderSettingsQuery } from '@/api/settings'
import { TextField } from '@/components/TextField'
import { Grid } from '@/components/Grid'
import SelectedMealDivisions from '@/features/MealPreferencesControl/SelectedMealDivisions'
import { DEFAULT_MEAL_DIVISIONS, SORTED_MEALS } from '@/features/meal-recommendations/constants'
import RecipeDashboard from '@/features/testing/InfoCards/InfoCards'
import MealOfDayDistinctRecipesChart from '@/features/testing/MealOfDayDistinctRecipesChart/MealOfDayDistinctRecipesChart'
import { CompactTable } from '@/components/Table'
import { Flex } from '@/components/Flex'
import { PageEditHeader } from '@/components/PageEditHeader'

export const Route = createFileRoute('/testing')({
  component: Index,
})

function Index() {
  const [recommenderSettings, setRecommenderSettings] = React.useState<
    RecommenderSettings | undefined
  >(undefined)

  const [generatorOptions, setGeneratorOptions] = React.useState<GeneratorOptions>({
    nrOfUsers: 100,
    minAge: 18,
    maxAge: 65,
    nrOfPlansPerUser: 3,
    mealOfDayDivisions: DEFAULT_MEAL_DIVISIONS,
  })

  const { data: systemRecommenderSettings } = useQuery(recommenderSettingsQuery)

  useEffect(() => {
    if (systemRecommenderSettings) {
      setRecommenderSettings(systemRecommenderSettings)
    }
  }, [systemRecommenderSettings])

  const [isLoading, setIsLoading] = React.useState(false)

  const { data, refetch } = useQuery(
    queryOptions({
      queryKey: ['testing_alg_query', recommenderSettings, generatorOptions],
      queryFn: () => runSimulations(recommenderSettings!, generatorOptions),
      enabled: false,
      refetchOnWindowFocus: false,
      staleTime: 60 * 60 * 1000,
    }),
  )

  async function runTests() {
    setIsLoading(true)
    await refetch()
    setIsLoading(false)
  }

  return (
    <>
      <PageEditHeader title="Testing algorithm" />
      <Flex gap={20} direction="column">
        {recommenderSettings && (
          <RecommenderSettingsForm
            recommenderSettings={recommenderSettings}
            setRecommenderSettings={setRecommenderSettings}
          />
        )}
        <GeneratorOptionsForm options={generatorOptions} setOptions={setGeneratorOptions} />
        <Flex direction="row" align="center">
          <Button variant="primary" onPress={() => runTests()}>
            Run tests
          </Button>
          {isLoading && <Spinner />}
        </Flex>
      </Flex>
      {data && <Dashboard data={data.data} />}
    </>
  )
}

function GeneratorOptionsForm({
  options,
  setOptions,
}: {
  options: GeneratorOptions
  setOptions: (options: GeneratorOptions) => void
}) {
  return (
    <div className="panel-xs">
      <h3>Generator options</h3>
      <Grid columns="1fr 1fr" gap={10} style={{ marginTop: '20px' }}>
        <Grid columns="1fr 1fr" gap={10} style={{ width: '50%' }}>
          <TextField
            isRequired={true}
            label="Number of users"
            type="number"
            value={options.nrOfUsers + ''}
            onChange={(value) => setOptions({ ...options, nrOfUsers: parseInt(value) })}
          />
          <TextField
            isRequired={true}
            label="Number of plans per user"
            type="number"
            value={options.nrOfPlansPerUser + ''}
            onChange={(value) => setOptions({ ...options, nrOfPlansPerUser: parseInt(value) })}
          />
          <TextField
            isRequired={true}
            label="Min age"
            type="number"
            value={options.minAge + ''}
            onChange={(value) => setOptions({ ...options, minAge: parseInt(value) })}
          />
          <TextField
            isRequired={true}
            label="Max age"
            type="number"
            value={options.maxAge + ''}
            onChange={(value) => setOptions({ ...options, maxAge: parseInt(value) })}
          />
        </Grid>
        <div>
          <Label>Daily meals</Label>
          <SelectedMealDivisions
            style={{ width: '760px', backgroundColor: 'white', padding: '10px' }}
            mealsOfDay={SORTED_MEALS}
            onChange={(mealOfDayDivisions) => setOptions({ ...options, mealOfDayDivisions })}
            mealOfDayDivisions={options.mealOfDayDivisions}
          />
        </div>
      </Grid>
    </div>
  )
}

function Dashboard({ data }: { data: TestingResponse }) {
  return (
    <>
      <RecipeDashboard {...data} />
      <div style={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>
        <DayDeviationChart data={data.mealsDayDeviations} />
        <Flex gap={20}>
          <DistinctRecipesChart data={data.distinctRecipesPerUser} />
          <MealOfDayDistinctRecipesChart data={data.distinctRecipesPerMoDPerUser} />
        </Flex>
        <NonRecommendedTable data={data.nonRecommendedRecipes} />
      </div>
    </>
  )
}

type ChartProps = {
  data: MealsDayDeviationRow[]
}

function DayDeviationChart({ data }: ChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const longData = data.flatMap((d) => [
    { numberOfMeals: d.numberOfMeals, nutrient: 'Kcal', deviation: d.avgDayKcalDeviation },
    { numberOfMeals: d.numberOfMeals, nutrient: 'Protein', deviation: d.avgDayProteinDeviation },
    { numberOfMeals: d.numberOfMeals, nutrient: 'Carbs', deviation: d.avgDayCarbsDeviation },
    { numberOfMeals: d.numberOfMeals, nutrient: 'Fat', deviation: d.avgDayFatDeviation },
  ])
  const mealsDomain = [3, 4, 5, 6]

  useEffect(() => {
    if (data === undefined) return
    const plot = Plot.plot({
      marks: [
        Plot.line(longData, {
          x: 'numberOfMeals',
          y: 'deviation',
          stroke: 'nutrient',
          strokeWidth: 2,
          // Optional: Add tooltips
          // title: d => `${d.nutrient}: ${d.deviation}%`
        }),
      ],
      x: {
        label: 'Number of Meals',
        type: 'band', // Treat x-axis as categorical
        domain: mealsDomain,
        // @ts-ignore this property is not defined in the type definition
        tickValues: mealsDomain,
        tickFormat: 'd', // Integer format
        grid: true,
      },
      y: {
        label: 'Average % Deviation',
        grid: true,
        domain: [-20, 100],
      },
      color: {
        legend: true,
        label: 'Nutrient',
        scheme: 'category10',
      },
      marginLeft: 60,
      marginBottom: 60,
      width: 800,
      height: 500,
      title: 'Average Macronutrient Deviations per Number of Meals',
      legend: true,
    })

    if (containerRef?.current) {
      containerRef.current.append(plot)
    }

    return () => plot.remove()
  }, [data])

  return <div className="panel-xs" ref={containerRef} />
}

function DistinctRecipesChart({ data }: { data: DistinctRecipes[] }) {
  const containerRef = useRef<HTMLDivElement>(null)

  const mealsDomain = [3, 4, 5, 6]

  useEffect(() => {
    if (data === undefined) return
    const plot = Plot.plot({
      marks: [
        Plot.ruleY([0]),
        Plot.boxY(data, { x: 'numberOfMeals', y: 'distinctRecipesPercentage' }),
      ],
      x: {
        label: 'Number of Meals',
        type: 'band',
        domain: mealsDomain,
        // @ts-ignore this property is not defined in the type definition
        tickValues: mealsDomain,
        tickFormat: 'd',
        grid: true,
      },
      y: {
        label: 'Distinct Recipes',
        grid: true,
        domain: [0, 100],
      },
      color: {
        legend: true,
        scheme: 'category10',
      },
      marginLeft: 60,
      marginBottom: 60,
      width: 800,
      height: 500,
      title: 'Distinct Recipes per Number of Meals',
      legend: true,
    })

    if (containerRef?.current) {
      containerRef.current.append(plot)
    }

    return () => plot.remove()
  }, [data])

  return <div className="panel-xs" ref={containerRef} />
}

function NonRecommendedTable({ data }: { data: NonRecommendedRecipe[] }) {
  const rows = data.map((r) => ({
    ...r,
    mealParts: r.mealParts.join(', '),
    carbPercentage: r.carbPercentage.toFixed(1),
    proteinPercentage: r.proteinPercentage.toFixed(1),
    fatPercentage: r.fatPercentage.toFixed(1),
  }))

  return (
    <div className="panel-xs">
      <h3>Non-recommended recipes</h3>
      <CompactTable
        style={{ marginTop: '20px' }}
        search={false}
        aria-label="Non-recommended recipes"
        columns={[
          { label: 'Name', key: 'name' },
          { label: 'Meal Parts', key: 'mealParts' },
          { label: 'Energy per serving (kcal)', key: 'energyPerServing' },
          { label: 'Protein (kcal %)', key: 'proteinPercentage' },
          { label: 'Carbs (kcal %)', key: 'carbPercentage' },
          { label: 'Fat (kcal %)', key: 'fatPercentage' },
        ]}
        rows={rows}
        onLinkAction={(row) => {
          return `/recipes/${row.idRecipe}`
        }}
      />
    </div>
  )
}
